import { useMemo, useState } from "react";
import { Button, ColumnPicker, ColumnPickerAddButton, Input, Label, Modal, type ColumnPickerGroup } from "@fox1ck-org/ui";
import { Columns3 } from "lucide-react";
import { toast } from "@fox1ck-org/ui/toast";
import { ReportViewDefinition, type ReportView, type StatsRow } from "@gen/tracker/v1/reporting_pb";

import { FormulaDialog } from "@/components/reports/formula-dialog";
import type { ReportingSchema } from "@/lib/services/reporting-schema-service";

import { useT, type TKey } from "@/lib/i18n";
import { useCreateReportView } from "@/lib/services/report-view-service";

import { PICKER_LAYOUT, columnHint, formulaHint, nextOrder, pickerGroups, type PickerGroupId } from "@/lib/column-picker";
import { toastFailure } from "@/lib/plan";

import { PRESET_SETS, type ListColumn, type SetId } from "./list-columns";

/**
 * «Колонки: Деньги» (board CampColumns): the package's ColumnPicker as a wide
 * popover under its button — the sets across the top (Деньги · Воронка ·
 * Качество трафика · Сравнение, the saved ones, «Мои»), the schema's columns
 * grouped by where the number comes from with what each counts on the right
 * (Трафик + Конверсии | Деньги + Свои),
 * «Свои» — the workspace's formulas, chosen like any other column — and
 * «Порядок»: the shown columns, dragged into the reader's order (each keeps
 * its band). Each column is its short name with what it counts on the right
 * (lib/column-picker, the same as the report's picker). «Сбросить» and
 * «Сохранить набор» stand on the sets' row; a saved set is a view of surface
 * «columns» on the server. A ticked column goes in beside its group's shown
 * neighbour, not at the end (lib/column-picker nextOrder). «+ Своя формула»
 * — the package's dashed pill — under «Порядок» opens the report
 * engine's formula dialog; the new metric arrives with the reloaded schema
 * and joins «Мои».
 */

/** The catalog as the picker's entries: the short name, the board's note, a formula's own expression. Pure. */
export function campaignPickerEntries(catalog: readonly ListColumn[], t: (k: TKey) => string) {
  const labelOf = (key: string) => catalog.find((c) => c.key === key)?.label;
  return catalog.map((c) => ({
    key: c.key,
    label: c.label,
    group: c.group,
    hint: c.group === "custom" ? (c.def?.formula?.expression ? formulaHint(c.def.formula.expression, labelOf) : t("cols.hint.formula")) : columnHint(t, c.key),
  }));
}

const GROUP_KEY: Record<PickerGroupId, "camps.band.traffic" | "camps.band.conversions" | "camps.cols.goals" | "camps.band.money" | "camps.band.custom"> = {
  traffic: "camps.band.traffic",
  conversions: "camps.band.conversions",
  money: "camps.band.money",
  custom: "camps.band.custom",
  goals: "camps.cols.goals",
};

export function CampaignColumns({
  label,
  iconOnly,
  catalog,
  shown,
  set,
  savedSets,
  onSet,
  onMine,
  schema,
  totals,
}: {
  /** The button's words: «Колонки: Деньги». */
  label: string;
  /** Board Camp1280: the button is its icon, the words its name and hint. */
  iconOnly?: boolean;
  catalog: readonly ListColumn[];
  /** The keys on screen now. */
  shown: readonly string[];
  set: SetId;
  savedSets: readonly ReportView[];
  onSet: (set: SetId) => void;
  /** The reader ticked, unticked or moved a column: the next «Мои», in order. */
  onMine: (keys: string[]) => void;
  /** For «+ Своя формула»: the metrics it is built from and the list's totals it is checked on. */
  schema: ReportingSchema | undefined;
  totals: StatsRow | undefined;
}) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [naming, setNaming] = useState(false);
  const [formula, setFormula] = useState(false);
  const groups = useMemo<ColumnPickerGroup[]>(
    () =>
      pickerGroups(campaignPickerEntries(catalog, t), (g) => t(GROUP_KEY[g])),
    [catalog, t],
  );
  const visibility = useMemo(() => {
    const on = new Set(shown);
    return Object.fromEntries(catalog.map((c) => [c.key, on.has(c.key)]));
  }, [catalog, shown]);
  const sets = [
    ...PRESET_SETS.map((id) => ({ id, name: t(`camps.set.${id}`) })),
    ...savedSets.map((v) => ({ id: `v:${v.id}`, name: v.name })),
    { id: "mine", name: t("camps.set.mine") },
  ];
  return (
    <>
      <ColumnPicker
        open={open}
        onOpenChange={setOpen}
        trigger={
          <Button variant="secondary" size={iconOnly ? "icon" : undefined} aria-expanded={open} aria-label={iconOnly ? label : undefined} title={iconOnly ? label : undefined}>
            <Columns3 className="size-4" strokeWidth={1.5} />
            {iconOnly ? null : label}
          </Button>
        }
        groups={groups}
        visibility={visibility}
        onVisibilityChange={(next) => onMine(nextOrder(shown, next, groups))}
        sets={sets}
        activeSetId={set}
        onSelectSet={(id) => onSet(id as SetId)}
        onSaveSet={() => setNaming(true)}
        onResetSet={() => onSet(set === "mine" ? "money" : set)}
        layout={PICKER_LAYOUT}
        actions={<ColumnPickerAddButton onClick={() => setFormula(true)}>{t("rep.formula.add")}</ColumnPickerAddButton>}
        order={shown}
        onOrderChange={(next) => onMine(next.filter((k) => shown.includes(k)))}
        labels={{
          title: t("camps.cols.title"),
          description: t("camps.cols.description"),
          sets: t("camps.cols.sets"),
          save: t("camps.cols.save"),
          reset: t("camps.cols.reset"),
          empty: t("camps.cols.empty"),
          close: t("camps.cols.close"),
          setsCaption: t("camps.cols.sets"),
          order: (count) => t("camps.cols.order", { count }),
          move: (name) => t("camps.cols.move", { name }),
          moved: (name, position, total) => t("camps.cols.moved", { name, position, total }),
        }}
      />
      <FormulaDialog schema={schema} rawTotals={totals} open={formula} onOpenChange={setFormula} onCreated={(key) => onMine([...shown, key])} />
      <SaveSetDialog open={naming} onClose={() => setNaming(false)} keys={shown} onSaved={(v) => onSet(`v:${v.id}`)} />
    </>
  );
}

function SaveSetDialog({
  open,
  onClose,
  keys,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  keys: readonly string[];
  onSaved: (view: ReportView) => void;
}) {
  const t = useT();
  const [name, setName] = useState("");
  const create = useCreateReportView();
  const save = () => {
    create.mutate(
      { name: name.trim(), workspaceShared: false, definition: new ReportViewDefinition({ surface: "columns", metricKeys: [...keys] }) },
      {
        onSuccess: (view) => {
          if (view) onSaved(view);
          toast.success(t("camps.cols.saved", { name: view?.name ?? name.trim() }));
          setName("");
          onClose();
        },
        onError: (e) => toastFailure(e, t("rep.saveFailed")),
      },
    );
  };
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t("camps.cols.saveTitle")}
      description={t("camps.cols.saveDescription")}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t("camps.cancel")}
          </Button>
          <Button onClick={save} disabled={!name.trim() || create.isPending}>
            {t("camps.cols.save")}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="camps-set-name" textCase="none">
          {t("camps.view.name")}
        </Label>
        <Input id="camps-set-name" font="sans" autoFocus value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && name.trim() && save()} />
      </div>
    </Modal>
  );
}
